import {
  LANE_IDS,
  type GameState,
  type LaneId,
  type NoTerritoryWarning,
  type PlayerId,
  type PlayerLaneState,
} from "../types";
import { getOpponentPlayerId } from "../types";

export type AttackKind = "direct" | "flank";

/** The sole authoritative definition of an Active Land. */
export function isActiveLandForPlayer(
  state: Readonly<GameState>,
  playerId: PlayerId,
  laneId: LaneId,
): boolean {
  const lane = state.lanes[laneId];
  const side = lane.players[playerId];
  return (
    lane.result === "active" &&
    side.status === "active" &&
    side.land !== null &&
    side.land.currentControl > 0
  );
}

/** A commander plus no Armies is a newly established formation; Armies alone are a garrison. */
export function isGarrisonEmpty(side: Readonly<PlayerLaneState>): boolean {
  return side.commander === null && side.armies.length === 0;
}

export function getActiveLandLaneIds(
  state: Readonly<GameState>,
  playerId: PlayerId,
): readonly LaneId[] {
  return LANE_IDS.filter((laneId) => isActiveLandForPlayer(state, playerId, laneId));
}

export function getAttackKind(
  state: Readonly<GameState>,
  playerId: PlayerId,
  sourceLaneId: LaneId,
  targetLaneId: LaneId,
): AttackKind | null {
  const opponentPlayerId = getOpponentPlayerId(playerId);
  if (!isActiveLandForPlayer(state, playerId, sourceLaneId)) return null;
  if (!isActiveLandForPlayer(state, opponentPlayerId, targetLaneId)) return null;
  if (isActiveLandForPlayer(state, opponentPlayerId, sourceLaneId)) {
    return targetLaneId === sourceLaneId ? "direct" : null;
  }
  return targetLaneId === sourceLaneId ? null : "flank";
}

export function getNoTerritoryWarningForPlayer(
  state: Readonly<GameState>,
  playerId: PlayerId,
): NoTerritoryWarning | null {
  return (state.schemaVersion === 4 || state.schemaVersion === 5) &&
    state.noTerritoryWarning?.threatenedPlayerId === playerId
    ? state.noTerritoryWarning
    : null;
}
